import {
  getIndicatorComplementaryAmounts,
  getIndicatorComplementaryActivitiesStates,
  getIndicatorAdvancePayments,
} from "../../../../../../rest/indicator";
import { makeWorkbook } from "../../utils/excel";

const round2 = (n) => Math.round((Number(n || 0) + Number.EPSILON) * 100) / 100;

const TYPE_LABEL = {
  BOOTCAMP: "Bootcamp",
  DIDACTIC: "Didático",
  DISCLOSURE: "Divulgação",
};

const STATE_LABEL = {
  APPROVED: "Aprovado",
  REJECTED: "Rejeitado",
  SUBMITTED: "Submetido",
  WAIT_CONTESTATION: "A aguardar contestação",
};

async function fetchComplementaryData() {
  const [resMontantes, resEstados, resAdiant] = await Promise.all([
    getIndicatorComplementaryAmounts(),
    getIndicatorComplementaryActivitiesStates(),
    getIndicatorAdvancePayments(),
  ]);

  const pickList = (resp) => {
    const p = resp?.data ?? {};
    if (Array.isArray(p?.data)) return p.data;
    if (Array.isArray(p?.results)) return p.results;
    if (Array.isArray(resp?.data)) return resp.data;
    return [];
  };

  const mList = pickList(resMontantes).map((r) => ({
    year: Number(r?.year ?? r?.ano ?? 0),
    type: r?.type ?? r?.tipo ?? "",
    value: Number(r?.value ?? r?.valueSum ?? r?.valor ?? 0),
    onlineValue: Number(
      r?.onlineValue ?? r?.onlineValueSum ?? r?.valorOnline ?? 0
    ),
  }));

  const eList = pickList(resEstados).map((r) => ({
    state: r?.state ?? r?.estado ?? "",
    total: Number(r?.total ?? 0),
    processReferences: Array.isArray(r?.processReferences)
      ? r.processReferences
      : [],
  }));

  const aList = pickList(resAdiant).map((r) => ({
    year: Number(r?.year ?? r?.ano ?? 0),
    valorAdiantamento: Number(
      r?.valorAdiantamento ?? r?.valorAdiantamentoSum ?? 0
    ),
    valorRemanescente: Number(
      r?.valorRemanescente ?? r?.valorRemanescenteSum ?? 0
    ),
    valorARestituir: Number(r?.valorARestituir ?? r?.valorARestituirSum ?? 0),
  }));

  return { montantes: mList, estados: eList, adiantamentos: aList };
}

export async function buildWorkbookComplementary() {
  const { montantes, estados, adiantamentos } = await fetchComplementaryData();

  const montantesByYear = (() => {
    const map = new Map();
    for (const m of montantes) {
      const y = m.year;
      if (!map.has(y))
        map.set(y, { year: y, rows: [], totalSolic: 0, totalAprov: 0 });
      const bucket = map.get(y);
      const vSolic = Number(m.value || 0);
      const vAprov = Number(m.onlineValue || 0);
      bucket.rows.push({
        type: m.type,
        typeLabel: TYPE_LABEL[m.type] || m.type,
        solicitado: vSolic,
        aprovado: vAprov,
      });
      bucket.totalSolic += vSolic;
      bucket.totalAprov += vAprov;
    }
    const arr = Array.from(map.values()).sort((a, b) => a.year - b.year);
    arr.forEach((y) =>
      y.rows.sort((a, b) => a.typeLabel.localeCompare(b.typeLabel))
    );
    return arr;
  })();

  const montantesKpis = (() => {
    const totalSolicitado = montantes.reduce(
      (s, m) => s + Number(m.value || 0),
      0
    );
    const totalAprovado = montantes.reduce(
      (s, m) => s + Number(m.onlineValue || 0),
      0
    );
    const taxa =
      totalSolicitado > 0 ? (totalAprovado / totalSolicitado) * 100 : 0;
    return { totalSolicitado, totalAprovado, taxa };
  })();

  const adiantKpis = (() => {
    const adiantado = adiantamentos.reduce(
      (s, a) => s + Number(a.valorAdiantamento || 0),
      0
    );
    const remanescente = adiantamentos.reduce(
      (s, a) => s + Number(a.valorRemanescente || 0),
      0
    );
    const aRestituir = adiantamentos.reduce(
      (s, a) => s + Number(a.valorARestituir || 0),
      0
    );
    return { adiantado, remanescente, aRestituir };
  })();

  const montantesDetalhe = [
    ["Ano", "Tipo", "Valor Solicitado", "Valor Aprovado"],
    ...montantes
      .slice()
      .sort(
        (a, b) =>
          a.year - b.year || String(a.type).localeCompare(String(b.type))
      )
      .map((m) => [
        m.year || "",
        TYPE_LABEL[m.type] || m.type || "",
        Number(m.value || 0),
        Number(m.onlineValue || 0),
      ]),
  ];

  const montantesAno = [
    ["Ano", "Total Solicitado", "Total Aprovado", "Taxa de Aprovação (%)"],
    ...montantesByYear.map((y) => [
      y.year,
      Number(y.totalSolic),
      Number(y.totalAprov),
      y.totalSolic > 0 ? round2((y.totalAprov / y.totalSolic) * 100) : 0,
    ]),
  ];

  const estadosSheet = [
    ["Estado", "Número de EAs", "Referências dos processos"],
    ...estados.map((e) => [
      STATE_LABEL[e.state] || e.state || "",
      Number(e.total || 0),
      (e.processReferences || []).join(", "),
    ]),
  ];

  const adiantSheet = [
    ["Ano", "Valor Adiantado", "Valor Remanescente", "Valor a Restituir"],
    ...adiantamentos
      .slice()
      .sort((a, b) => a.year - b.year)
      .map((a) => [
        a.year || "",
        Number(a.valorAdiantamento || 0),
        Number(a.valorRemanescente || 0),
        Number(a.valorARestituir || 0),
      ]),
  ];

  const totalEAs = estados.reduce((s, e) => s + Number(e.total || 0), 0);
  const resumo = [
    ["Indicador", "Valor"],
    ["Montantes - Total Solicitado", Number(montantesKpis.totalSolicitado)],
    ["Montantes - Total Aprovado", Number(montantesKpis.totalAprovado)],
    ["Montantes - Taxa de Aprovação (%)", round2(montantesKpis.taxa) || 0],
    ["Estados - Total de EAs", totalEAs],
    ["Adiantamentos - Valor Adiantado", Number(adiantKpis.adiantado)],
    ["Adiantamentos - Valor Remanescente", Number(adiantKpis.remanescente)],
    ["Adiantamentos - Valor a Restituir", Number(adiantKpis.aRestituir)],
  ];

  return makeWorkbook([
    { name: "Resumo", aoa: resumo },
    { name: "Montantes_Ano", aoa: montantesAno },
    { name: "Montantes_Detalhe", aoa: montantesDetalhe },
    { name: "Estados", aoa: estadosSheet },
    { name: "Adiantamentos", aoa: adiantSheet },
  ]);
}
